
import { Component, Ref } from 'vue-property-decorator';
import { State } from 'vuex-class';

// API
import { endReplacement, getReplacements, removeReplacement, updateReplacement } from '@/utils/api/UserReplacement';

// Components
import VAdministrationMain from '@/components/Administration/VAdministrationMain.vue';
import VAdministrationHeader from '@/components/Administration/VAdministrationHeader.vue';
import VPageTitle from '@/components/Administration/VPageTitle.vue';
import VAdministrationActionbar from '@/components/Administration/VAdministrationActionbar.vue';
import VAdministrationPrimaryTable from '@/components/Administration/VAdministrationPrimaryTable.vue';

import VPoppedAlert from '@/components/VPoppedAlert.vue';

// Mixins
import VAdministrationPrimaryTableMixin from '@/mixins/VAdministrationPrimaryTableMixin';

// Enums
import AdministrationPageNamesEnum from '@/ship/Enums/AdministrationPageNamesEnum';
import ModalEnum from '@/ship/Enums/ModalEnum';
import PermissionsEnum from '@/ship/Enums/PermissionsEnum';

// Interfaces
import { GetRequestParams } from '@/ship/Requests/RequestParams';
import { Dictionary } from 'lodash';
import { DateTime } from 'luxon';
import User from '@/ship/Models/User';
import { IReplacement, IReplacementEditModel, ReplacementStatus } from '@/ship/Models/IReplacement';

// Services
import ErrorService from '@/utils/ErrorService';

// Utils
import { hasPermission } from '@/utils/Permissions';
import { formatToSendAny } from '@/utils/ModelPacking';

@Component({
    components: {
        VAdministrationMain,
        VAdministrationHeader,
        VPageTitle,
        VAdministrationActionbar,
        VAdministrationPrimaryTable,
        VPoppedAlert,
    },
})
export default class ReplacementPage extends VAdministrationPrimaryTableMixin {
    @State user!: User;

    @Ref() public poppedAlert!: VPoppedAlert;

    readonly currentPageName = AdministrationPageNamesEnum.Replacement;
    readonly request = getReplacements;

    declare items: IReplacement[];

    get transformedReplacements() {
        return this.items.map(({ id, user, affiluser, from, to, description, status }) => {
            return {
                id,
                user: user?.data.name,
                affiluser: affiluser?.data.name,
                from: DateTime.fromJSDate(new Date(from)).toLocaleString(),
                to: DateTime.fromJSDate(new Date(to)).toLocaleString(),
                description,
                undelitable: !this.canRemoveReplacement(from, status, user?.data),
                uneditable: !this.canEditReplacement(status, user?.data),
                stoppable: this.canStopReplacement(status, user?.data),
            };
        });
    }

    public canRemoveReplacement(fromDate: string, status: ReplacementStatus, user?: User) {
        const today = DateTime.now().toFormat('yyyy-MM-dd');
        const from = DateTime.fromJSDate(new Date(fromDate)).toFormat('yyyy-MM-dd');
        const byDate = from > today;
        const byStatus = ReplacementStatus.Created === status;
        const byPermissions =
            hasPermission(PermissionsEnum.UsersAffil) ||
            (hasPermission(PermissionsEnum.UsersSelfAffil) && this.user.id === user?.id);
        return byDate && byStatus && byPermissions;
    }

    public canEditReplacement(status: ReplacementStatus, user?: User) {
        const byStatus = status === ReplacementStatus.Created || status === ReplacementStatus.Replaced;
        const byPermissions =
            hasPermission(PermissionsEnum.UsersAffil) ||
            (hasPermission(PermissionsEnum.UsersSelfAffil) && this.user.id === user?.id);
        return byStatus && byPermissions;
    }

    public canStopReplacement(status: ReplacementStatus, user?: User) {
        const byStatus = status === ReplacementStatus.Replaced;
        const byPermissions =
            hasPermission(PermissionsEnum.UsersAffil) ||
            (hasPermission(PermissionsEnum.UsersSelfAffil) && this.user.id === user?.id);
        return byStatus && byPermissions;
    }

    public getRequestParams(): GetRequestParams {
        const searchFilter = this.search
            ? `id,user.first_name,user.surname,user.patronymic,affiluser.first_name,affiluser.surname,affiluser.patronymic,description:${this.search};`
            : '';
        return { include: 'user,affiluser', search: searchFilter, sortedBy: 'desc' };
    }

    public async handleDeleteReplacement(row: Dictionary<string | number>) {
        const { id, user, affiluser } = row;

        this.$modal.show(ModalEnum.Confirm, {
            title: this.$t('page.administration.sureToDeleteReplacement', [user, affiluser]),
            onConfirm: async () => {
                await removeReplacement(id)
                    .then(async () => {
                        await this.updateItemsThrottled();
                        this.poppedAlert?.show(this.$t('page.administration.replacementDeleted'));
                    })
                    .catch(ErrorService.handleApiError);
            },
        });
    }

    public async handleStopReplacement(row: Dictionary<string | number>) {
        const { id, user, from, to } = row;

        const description = this.$t('page.administration.replacementWillStopNow', [user, from, to]);
        this.$modal.show(ModalEnum.Danger, {
            title: this.$t('page.administration.sureToStopReplacement'),
            description,
            acceptLabel: this.$t('complete'),
            onAccept: async () => {
                await endReplacement(id);
                await this.updateItemsThrottled();
                this.poppedAlert?.show(this.$t('page.administration.replacementStopped'));
            },
        });
    }
}
